import { ReplaceTokens, useTranslations } from '@vp/om-translations';
import {
  Box,
  Card,
  Column,
  GridContainer,
  H1,
  Row,
  Typography,
  useScreenClass,
} from '@vp/swan';
import React, { useContext, useEffect } from 'react';
import useSiteData from '../../../hooks/useSiteData';
import OrderDetailsCTA from './OrderDetailsCTA';
import { MemoizedDocPreview } from './DocPreview';
import useIdentityContext from '../../../hooks/useIdentityContext';
import { DESIGN_FEEDBACK_GROUP_CONFIRMATION_PAGE } from '../../../constants/orderdetailsui';
import { trackDesignFeedbackPageViewed } from '../../../apis/newRelic/designFeedbackPageNewRelicTracking';
import DesignFeedbackGroupRequestContext from '../../contexts/DesignFeedbackGroupRequestContext';
import { DesignFeedbackGroupResponseContext } from '../../contexts/DesignFeedbackGroupResponseContext';
import useShopperProfile from '../../../hooks/useShopperProfile';

/**
 * This component displays a confirmation that we have received the customer's feedback
 * in response to a design feedback request.
 *
 * It is rendered in two scenarios:
 * 1. Immediately after a user has submitted feedback for a "pending" request
 * 2. On load of a "processing" request
 *
 * In the first scenario, we are aware of the feedback they recently submitted in the existing session,
 * so we show them a summary of that feedback.
 *
 * In the second scenario, we are not aware of the feedback they previously submitted,
 * so we show a generic confirmation message.
 */
const DesignFeedbackConfirmationPage = () => {
  /**
   * As it is being used today, this component is being rendered on close of a modal and is intended to fill the entire page.
   * Whatever position the user scrolled to on the page/component that launched that modal is where they will remain once it closes.
   * So, if the user scrolled to the very bottom of the window when they launched the modal, they will remain there when it closes,
   * and find themselves looking at the bottom half of this page/component when it loads. This is janky as heck!
   * We prevent this by using window.scrollTo(0,0) to scroll them back to the top of the window on load of this component.
   */
  window.scrollTo(0, 0);

  const screenClass = useScreenClass();

  const [designFeedbackMessages] = useTranslations(['designFeedbackMessages']);

  const { vistacartTenant } = useSiteData();

  const { identity } = useIdentityContext();
  const { shopperId } = identity;

  const {
    orderId,
    groupId,
    feedbackGroupState,
    orderPlacedAnonymously,
    feedbackRequestId,
    originalDesign,
    emailAddress,
  } = useContext(DesignFeedbackGroupRequestContext);

  const profile = useShopperProfile();
  const email = emailAddress || profile?.email || '';

  const {
    isApproved,
    isRejected,
    selectedDesignOption,
    selectedDesignOptionId,
    selectedDesignOptionPreviews,
    selectedStatus,
    requestedChanges,
    isFeedbackSubmitted,
  } = useContext(DesignFeedbackGroupResponseContext);

  useEffect(() => {
    const trackingData = {
      groupId,
      orderNumber: orderId,
      feedbackGroupState,
      selectedStatus,
      selectedOptionId: selectedDesignOptionId,
      requestedChanges,
      orderPlacedAnonymously,
      tenant: vistacartTenant,
      isReturningCustomer: !isFeedbackSubmitted,
    };

    trackDesignFeedbackPageViewed(
      shopperId,
      DESIGN_FEEDBACK_GROUP_CONFIRMATION_PAGE,
      trackingData
    );
  }, [
    orderId,
    groupId,
    orderPlacedAnonymously,
    requestedChanges,
    shopperId,
    feedbackGroupState,
    vistacartTenant,
    selectedStatus,
    selectedDesignOption,
    feedbackRequestId,
    selectedDesignOptionId,
    isFeedbackSubmitted,
  ]);

  let confirmationTitle;
  let confirmationMessage;
  let feedbackSummary;

  // If we are aware of the customer feedback (because it was submitted in this same session),
  // show a specific confirmation message along with a summary of that feedback
  if (isApproved) {
    confirmationTitle = designFeedbackMessages.designApprovedTitle;
    confirmationMessage = (
      <ReplaceTokens
        tokenizedString={designFeedbackMessages.designApprovedMessage}
        substitutions={{
          email,
        }}
      />
    );
    feedbackSummary = (
      <MemoizedDocPreview
        previews={selectedDesignOptionPreviews}
        optionId={selectedDesignOptionId}
        alt={designFeedbackMessages.approvedDesign}
        vertical
      />
    );
  } else if (isRejected) {
    confirmationTitle = designFeedbackMessages.requestChangeTitle;
    confirmationMessage = designFeedbackMessages.requestChangeMessage;
    feedbackSummary = (
      <MemoizedDocPreview
        previews={originalDesign.previews}
        optionId={selectedDesignOptionId}
        alt={designFeedbackMessages.originalLabel}
        vertical
      />
    );
  }
  // If we are not aware of the customer feedback (because it was submitted in a different session),
  // show a generic confirmation message
  else {
    confirmationTitle = designFeedbackMessages.feedbackSubmissionProcessing;
    confirmationMessage = null;
    feedbackSummary = (
      <MemoizedDocPreview
        previews={originalDesign.previews}
        optionId={selectedDesignOptionId}
        alt={designFeedbackMessages.originalLabel}
        vertical
      />
    );
  }

  return (
    <Box my={7}>
      <GridContainer>
        <Row>
          {feedbackSummary ? (
            <Column span={4} offset={1}>
              <Card p={6} evenHeight bordered>
                {feedbackSummary}
              </Card>
            </Column>
          ) : null}
          <Column span={6}>
            <H1 fontSize="x3large" mt={6} mb={6}>
              {confirmationTitle}
            </H1>
            <Typography>{confirmationMessage}</Typography>
            <OrderDetailsCTA
              orderId={orderId}
              skin="secondary"
              width={['xs'].includes(screenClass) ? 'full-width' : 'standard'}
              my={6}
            />
          </Column>
        </Row>
      </GridContainer>
    </Box>
  );
};

export default DesignFeedbackConfirmationPage;
